import { useState, useEffect, useCallback } from "react";
import { Trophy, Users, Calendar } from "lucide-react";
import { CompetitionsAPI } from "../../APIs/competitions.api";
import { CompetitionResults } from "../../types/competition.types";
import { useCompetitions } from "../../context/CompetitionsContext";
import { Loading } from "../../components/loading";
import { TeamCategoryId } from "../../types/team_categories.types";

export function CompetitionResultsUsers() {
  const [results, setResults] = useState<CompetitionResults>({
    overall_results: [],
    category_results: [],
    biggest_fish_results: [],
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const { currentCompetitionId } = useCompetitions();

  const loadResults = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await CompetitionsAPI.getCompetitionResults(
        currentCompetitionId || 1
      );

      if (response.statusCode === 200) {
        const data = response.data;
        if (Array.isArray(data) && data.length === 0) {
          setResults({
            overall_results: [],
            category_results: [],
            biggest_fish_results: [],
          });
        } else {
          setResults(
            data || {
              overall_results: [],
              category_results: [],
              biggest_fish_results: [],
            }
          );
        }
      } else {
        setError("Error al cargar los resultados");
      }
    } catch (error) {
      setError("Error al cargar los resultados");
    } finally {
      setLoading(false);
    }
  }, [currentCompetitionId]);

  useEffect(() => {
    loadResults();
  }, [loadResults]);

  const hasResults = () => {
    return (
      (results.overall_results && results.overall_results.length > 0) ||
      (results.biggest_fish_results &&
        results.biggest_fish_results.length > 0) ||
      (results.category_results && results.category_results.length > 0)
    );
  };

  if (loading) {
    return <Loading />;
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <Trophy className="h-16 w-16 text-gray-400 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-gray-900 mb-2">
            Error al cargar resultados
          </h2>
          <p className="text-gray-600">
            Por favor, intenta nuevamente más tarde.
          </p>
        </div>
      </div>
    );
  }

  if (!hasResults()) {
    return (
      <div className="mt-4 flex items-center justify-center">
        <div className="text-center max-w-md mx-auto px-4">
          <Trophy className="h-20 w-20 text-gray-400 mx-auto mb-6" />
          <h2 className="text-3xl font-bold text-gray-900 mb-4">
            Resultados no disponibles
          </h2>
          <div className="space-y-3 text-gray-600">
            <p className="flex items-center justify-center">
              <Calendar className="h-5 w-5 mr-2" />
              La competencia aún no ha finalizado
            </p>
            <p className="text-center">o</p>
            <p className="flex items-center justify-center">
              <Users className="h-5 w-5 mr-2" />
              Los resultados oficiales no han sido publicados
            </p>
          </div>
          <p className="mt-6 text-sm text-gray-500">
            Los resultados se publicarán una vez que la competencia haya
            finalizado y los organizadores hayan verificado todos los datos.
          </p>
        </div>
      </div>
    );
  }

  const sortedOverallResults = (results.overall_results || []).sort(
    (a, b) => a.final_position - b.final_position
  );

  return (
    <div className="min-h-screen bg-gray-50 py-4 sm:py-8">
      <div className="max-w-7xl mx-auto px-2 sm:px-4 lg:px-8">
        {/* Header */}
        <div className="text-center mb-6 sm:mb-8 relative">
          <div className="flex justify-center mb-4">
            <div className="bg-blue-600 p-2 sm:p-3 rounded-full">
              <Trophy className="h-8 w-8 sm:h-12 sm:w-12 text-white" />
            </div>
          </div>
          <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-gray-900 mb-2 px-4">
            Pesca Variada
          </h1>
          <h2 className="text-lg sm:text-xl lg:text-2xl font-semibold text-blue-600 mb-4">
            RESULTADOS OFICIALES
          </h2>
        </div>

        <div className="space-y-6 sm:space-y-8 mb-14">
          {/* Overall Results Table */}
          <div className="bg-white rounded-lg shadow-md overflow-hidden">
            <div className="bg-blue-600 px-4 sm:px-6 py-3 sm:py-4">
              <h3 className="text-lg sm:text-xl font-bold text-white text-center">
                RESULTADOS
              </h3>
            </div>
            <div className="overflow-x-auto">
              <table className="min-w-full border-collapse">
                <thead>
                  <tr className="bg-blue-50">
                    <th className="border border-gray-300 px-2 sm:px-4 py-2 sm:py-3 text-center text-xs sm:text-sm font-semibold text-gray-700 uppercase">
                      Puesto
                    </th>
                    <th className="border border-gray-300 px-2 sm:px-4 py-2 sm:py-3 text-center text-xs sm:text-sm font-semibold text-gray-700 uppercase">
                      Equipo
                    </th>
                    <th className="border border-gray-300 px-2 sm:px-4 py-2 sm:py-3 text-center text-xs sm:text-sm font-semibold text-gray-700 uppercase hidden sm:table-cell">
                      Participantes
                    </th>
                    <th className="border border-gray-300 px-2 sm:px-4 py-2 sm:py-3 text-center text-xs sm:text-sm font-semibold text-gray-700 uppercase">
                      Puntaje
                    </th>
                    <th className="border border-gray-300 px-2 sm:px-4 py-2 sm:py-3 text-center text-xs sm:text-sm font-semibold text-gray-700 uppercase hidden md:table-cell">
                      Especies
                    </th>
                    <th className="border border-gray-300 px-2 sm:px-4 py-2 sm:py-3 text-center text-xs sm:text-sm font-semibold text-gray-700 uppercase hidden md:table-cell">
                      Capturas
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {sortedOverallResults.map((result, index) => (
                    <tr
                      key={result.id}
                      className={index % 2 === 0 ? "bg-white" : "bg-gray-50"}
                    >
                      <td className="border border-gray-300 px-2 sm:px-4 py-2 sm:py-3 text-center">
                        <span
                          className={`inline-flex items-center justify-center w-6 h-6 sm:w-8 sm:h-8 rounded-full text-xs sm:text-sm font-bold ${
                            index === 0
                              ? "bg-yellow-500 text-white"
                              : index === 1
                              ? "bg-gray-400 text-white"
                              : index === 2
                              ? "bg-yellow-600 text-white"
                              : "bg-gray-200 text-gray-700"
                          }`}
                        >
                          {result.final_position}°
                        </span>
                      </td>
                      <td className="border border-gray-300 px-2 sm:px-4 py-2 sm:py-3 text-center font-medium">
                        <div className="font-bold text-gray-900 text-xs sm:text-sm flex items-center justify-center gap-2">
                          <span>{result.team.name}</span>
                          <span className="bg-blue-100 text-blue-800 px-1.5 py-0.5 rounded text-xs font-medium">
                            #{result.team.team_number}
                          </span>
                        </div>
                        {result.team.institution_name && (
                          <div className="text-gray-600 text-xs mt-1 hidden sm:block">
                            {result.team.institution_name}
                          </div>
                        )}
                        <div className="sm:hidden mt-1">
                          {result.team.members &&
                            result.team.members.length > 0 && (
                              <div className="text-xs text-gray-600">
                                {result.team.members.map(
                                  (member, memberIndex) => (
                                    <div key={member.id}>
                                      {member.first_name} {member.last_name}
                                    </div>
                                  )
                                )}
                              </div>
                            )}
                        </div>
                      </td>
                      <td className="border border-gray-300 px-2 sm:px-4 py-2 sm:py-3 text-center hidden sm:table-cell">
                        <div className="text-sm">
                          {result.team.members &&
                            result.team.members.length > 0 && (
                              <div>
                                {result.team.members.map(
                                  (member, memberIndex) => (
                                    <div
                                      key={member.id}
                                      className="font-medium text-xs"
                                    >
                                      {member.first_name} {member.last_name}
                                    </div>
                                  )
                                )}
                              </div>
                            )}
                        </div>
                      </td>
                      <td className="border border-gray-300 px-2 sm:px-4 py-2 sm:py-3 text-center">
                        <span className="text-sm sm:text-lg font-bold text-blue-700">
                          {result.points}
                        </span>
                        <div className="md:hidden mt-1 text-xs text-gray-500">
                          {result.species_count}esp • {result.pieces_count}cap
                        </div>
                      </td>
                      <td className="border border-gray-300 px-2 sm:px-4 py-2 sm:py-3 text-center hidden md:table-cell text-sm">
                        {result.species_count}
                      </td>
                      <td className="border border-gray-300 px-2 sm:px-4 py-2 sm:py-3 text-center hidden md:table-cell text-sm">
                        {result.pieces_count}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Biggest Fish Results Table */}
          {results.biggest_fish_results &&
            results.biggest_fish_results.length > 0 && (
              <div className="bg-white rounded-lg shadow-md overflow-hidden">
                <div className="bg-blue-600 px-4 sm:px-6 py-3 sm:py-4">
                  <h3 className="text-base sm:text-lg font-bold text-white text-center">
                    PIEZA MAYOR
                  </h3>
                </div>
                <div className="overflow-x-auto">
                  <table className="min-w-full border-collapse">
                    <thead>
                      <tr className="bg-blue-50">
                        <th className="border border-gray-300 px-2 sm:px-3 py-2 text-center text-xs font-semibold text-gray-700 uppercase">
                          Piezas
                        </th>
                        <th className="border border-gray-300 px-2 sm:px-3 py-2 text-center text-xs font-semibold text-gray-700 uppercase">
                          Equipo
                        </th>
                        <th className="border border-gray-300 px-2 sm:px-3 py-2 text-center text-xs font-semibold text-gray-700 uppercase hidden sm:table-cell">
                          Participante
                        </th>
                        <th className="border border-gray-300 px-2 sm:px-3 py-2 text-center text-xs font-semibold text-gray-700 uppercase">
                          Puntaje
                        </th>
                        <th className="border border-gray-300 px-2 sm:px-3 py-2 text-center text-xs font-semibold text-gray-700 uppercase">
                          CM
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {results.biggest_fish_results.map((result, index) => (
                        <tr
                          key={result.id}
                          className={
                            index % 2 === 0 ? "bg-white" : "bg-gray-50"
                          }
                        >
                          <td className="border border-gray-300 px-2 sm:px-3 py-2 text-center font-semibold text-xs sm:text-sm">
                            {result.species.name}
                          </td>
                          <td className="border border-gray-300 px-2 sm:px-3 py-2 text-center text-xs sm:text-sm">
                            <div className="font-bold text-gray-900 flex items-center justify-center gap-1 flex-wrap">
                              <span>{result.team.name}</span>
                              <span className="bg-blue-100 text-blue-800 px-1 py-0.5 rounded text-xs font-medium">
                                #{result.team.team_number}
                              </span>
                            </div>
                            {result.team.institution_name && (
                              <div className="text-xs text-gray-600 hidden sm:block">
                                {result.team.institution_name}
                              </div>
                            )}
                            <div className="sm:hidden text-xs text-gray-600 mt-1">
                              {result.participant.first_name}{" "}
                              {result.participant.last_name}
                            </div>
                          </td>
                          <td className="border border-gray-300 px-2 sm:px-3 py-2 text-center text-xs sm:text-sm hidden sm:table-cell">
                            {result.participant.first_name}{" "}
                            {result.participant.last_name}
                          </td>
                          <td className="border border-gray-300 px-2 sm:px-3 py-2 text-center text-xs sm:text-sm font-bold text-blue-700">
                            {result.points}
                          </td>
                          <td className="border border-gray-300 px-2 sm:px-3 py-2 text-center text-xs sm:text-sm font-bold">
                            {result.size_cm}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

          {/* Category Results Table */}
          {results.category_results && results.category_results.length > 0 && (
            <div className="bg-white rounded-lg shadow-md overflow-hidden">
              <div className="bg-blue-600 px-4 sm:px-6 py-3 sm:py-4">
                <h3 className="text-base sm:text-lg font-bold text-white text-center">
                  GRUPOS
                </h3>
              </div>
              <div className="overflow-x-auto">
                <table className="min-w-full border-collapse">
                  <thead>
                    <tr className="bg-blue-50">
                      <th className="border border-gray-300 px-2 sm:px-3 py-2 text-center text-xs font-semibold text-gray-700 uppercase">
                        Grupo
                      </th>
                      <th className="border border-gray-300 px-2 sm:px-3 py-2 text-center text-xs font-semibold text-gray-700 uppercase">
                        Equipo
                      </th>
                      <th className="border border-gray-300 px-2 sm:px-3 py-2 text-center text-xs font-semibold text-gray-700 uppercase hidden sm:table-cell">
                        Participantes
                      </th>
                      <th className="border border-gray-300 px-2 sm:px-3 py-2 text-center text-xs font-semibold text-gray-700 uppercase">
                        Puntaje
                      </th>
                      <th className="border border-gray-300 px-2 sm:px-3 py-2 text-center text-xs font-semibold text-gray-700 uppercase hidden md:table-cell">
                        Especies
                      </th>
                      <th className="border border-gray-300 px-2 sm:px-3 py-2 text-center text-xs font-semibold text-gray-700 uppercase hidden md:table-cell">
                        Piezas
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {results.category_results.map((result, index) => (
                      <tr
                        key={result.id}
                        className={index % 2 === 0 ? "bg-white" : "bg-gray-50"}
                      >
                        <td className="border border-gray-300 px-2 sm:px-3 py-2 text-center">
                          <div className="font-semibold text-xs bg-blue-100 text-blue-800 px-1 sm:px-2 py-1 rounded">
                            {result.category.name}
                          </div>
                          <div className="text-xs text-gray-600 mt-1 hidden sm:block">
                            {result.category.id === TeamCategoryId.MENOR
                              ? "Mejor clasificado"
                              : "Mejor clasificada"}
                          </div>
                        </td>
                        <td className="border border-gray-300 px-2 sm:px-3 py-2 text-center text-xs sm:text-sm font-medium">
                          <div className="font-bold text-gray-900 flex items-center justify-center gap-1 flex-wrap">
                            <span>{result.team.name}</span>
                            <span className="bg-blue-100 text-blue-800 px-1 py-0.5 rounded text-xs font-medium">
                              #{result.team.team_number}
                            </span>
                          </div>
                          {result.team.institution_name && (
                            <div className="text-xs text-gray-600 hidden sm:block">
                              {result.team.institution_name}
                            </div>
                          )}
                          <div className="sm:hidden mt-1">
                            {result.team.members &&
                              result.team.members.length > 0 && (
                                <div className="text-xs text-gray-600">
                                  {result.team.members.map(
                                    (member, memberIndex) => (
                                      <div key={member.id}>
                                        {member.first_name} {member.last_name}
                                      </div>
                                    )
                                  )}
                                </div>
                              )}
                          </div>
                        </td>
                        <td className="border border-gray-300 px-2 sm:px-3 py-2 text-center text-xs sm:text-sm hidden sm:table-cell">
                          <div>
                            {result.team.members &&
                              result.team.members.length > 0 && (
                                <div>
                                  {result.team.members.map(
                                    (member, memberIndex) => (
                                      <div
                                        key={member.id}
                                        className="font-medium text-xs"
                                      >
                                        {member.first_name} {member.last_name}
                                      </div>
                                    )
                                  )}
                                </div>
                              )}
                          </div>
                        </td>
                        <td className="border border-gray-300 px-2 sm:px-3 py-2 text-center text-xs sm:text-sm font-bold text-blue-700">
                          {result.points}
                          <div className="md:hidden text-xs text-gray-500 mt-1">
                            {result.species_count}e • {result.pieces_count}p
                          </div>
                        </td>
                        <td className="border border-gray-300 px-2 sm:px-3 py-2 text-center text-xs sm:text-sm hidden md:table-cell">
                          {result.species_count}
                        </td>
                        <td className="border border-gray-300 px-2 sm:px-3 py-2 text-center text-xs sm:text-sm hidden md:table-cell">
                          {result.pieces_count}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
